import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { ServiceProgress } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, 
  Clock, 
  PlayCircle, 
  ArrowRight, 
  BarChart, 
  Settings, 
  Calendar, 
  Users,
  Target,
  ChevronRight,
  AlertCircle,
  Trash2,
  Loader2
} from 'lucide-react';
import Button from '../components/ui/Button';

interface DashboardProps {
  activeTab?: string;
}

export default function Dashboard({ activeTab = 'overview' }: DashboardProps) {
  const [serviceProgress, setServiceProgress] = useState<ServiceProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    active_services: 0,
    completed_services: 0,
    days_active: 0,
    average_progress: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchInitialData();
    const channels = setupRealtimeSubscriptions();

    return () => {
      channels.forEach(channel => {
        supabase.channel(channel).unsubscribe();
      });
    };
  }, []);

  async function checkAuth() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
      return;
    }
  }

  async function fetchInitialData() {
    try {
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Fetch dashboard metrics
      const { data: metricsData, error: metricsError } = await supabase
        .rpc('get_dashboard_metrics');

      if (metricsError) throw metricsError;
      setMetrics(metricsData);

      // Fetch service progress
      const { data: progress, error: progressError } = await supabase
        .from('service_progress')
        .select(`
          *,
          services (
            name,
            description
          )
        `)
        .eq('user_id', user.id)
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (progressError) throw progressError;
      setServiceProgress(progress || []);

      try {
        // Fetch notifications
        const { data: notifs, error: notifsError } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5);

        if (notifsError) throw notifsError;
        setNotifications(notifs || []);
      } catch (notifError) {
        console.warn('Failed to fetch notifications:', notifError);
        // Don't throw error for notifications - non-critical feature
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function setupRealtimeSubscriptions() {
    const channelNames = ['service_progress_changes', 'notifications_changes'];

    // Subscribe to service progress changes
    supabase
      .channel('service_progress_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'service_progress'
        },
        async (payload) => {
          console.log('Service progress changed:', payload);
          await fetchInitialData();
        }
      )
      .subscribe();

    // Subscribe to notifications
    supabase
      .channel('notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        async (payload) => {
          console.log('Notification received:', payload);
          await fetchInitialData();
        }
      )
      .subscribe();

    return channelNames;
  }

  const handleRemoveService = async (serviceId: string) => {
    try {
      setRemoving(serviceId);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update service to inactive instead of deleting
      const { error } = await supabase
        .from('service_progress')
        .update({ active: false })
        .eq('id', serviceId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setServiceProgress(prev => prev.filter(service => service.id !== serviceId));

    } catch (error) {
      console.error('Error removing service:', error);
      setError('Failed to remove service. Please try again.');
    } finally {
      setRemoving(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'in_progress':
        return <PlayCircle className="w-6 h-6 text-blue-500" />;
      default:
        return <Clock className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 text-green-700';
      case 'in_progress':
        return 'bg-blue-50 text-blue-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateRemainingDays = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatBusinessDetails = (details: any) => {
    if (!details) return null;
    
    const labels = {
      type: 'Business Type',
      size: 'Company Size',
      industry: 'Industry',
      schedule: 'Schedule',
      startDate: 'Start Date',
      name: 'Contact Name',
      phoneNumber: 'Phone Number'
    };

    return Object.entries(details).map(([key, value]) => ({
      label: labels[key as keyof typeof labels],
      value: String(value).split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <Button onClick={() => fetchInitialData()}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (serviceProgress.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Services Selected</h2>
          <p className="text-gray-600 mb-8">Get started by selecting a service from our offerings.</p>
          <Button onClick={() => navigate('/services')}>View Services</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Welcome Back!</h1>
              <p className="text-gray-600">Here's what's happening with your services today.</p>
            </div>
            <Button onClick={() => navigate('/services')} variant="outline">
              View Services
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <BarChart className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{metrics.active_services}</div>
                    <div className="text-sm text-gray-600">Active Services</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-green-50 rounded-lg p-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {metrics.completed_services}
                    </div>
                    <div className="text-sm text-gray-600">Completed</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-purple-50 rounded-lg p-3">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{metrics.days_active}</div>
                    <div className="text-sm text-gray-600">Days Active</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <Target className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{metrics.average_progress}%</div>
                    <div className="text-sm text-gray-600">Avg. Progress</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Service Progress */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Active Services</h2>
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
              
              {serviceProgress.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {service.services?.name}
                    </h2>
                    {getStatusIcon(service.status)}
                  </div>

                  <p className="text-gray-600 mb-6">{service.services?.description}</p>

                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-medium">{service.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${service.progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-600">
                          Started: {formatDate(service.start_date)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {calculateRemainingDays(service.end_date)} days remaining
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(service.status)}`}>
                          {service.status.replace('_', ' ').charAt(0).toUpperCase() + service.status.slice(1)}
                        </span>
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveService(service.id)}
                          disabled={removing === service.id}
                        >
                          {removing === service.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/services/${service.service_id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Side Panel */}
            <div className="space-y-6">
              {/* Business Details */}
              {serviceProgress[0]?.business_details && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Details</h3>
                  <div className="space-y-4">
                    {formatBusinessDetails(serviceProgress[0].business_details)?.map((detail, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-gray-600">{detail.label}</span>
                        <span className="font-medium text-gray-900">{detail.value}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate('/services')}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Services
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      const element = document.getElementById('consultation');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Consultation
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate('/support')}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}